import { useEffect } from "react";
import { CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { ServiceRequestForm } from "@/components/ServiceRequestForm";
import { FadeIn } from "@/components/Reveal";

const INCLUDES = [
  { title: "Business-focused screening packages", desc: "Screening options designed around the roles your business actually hires for." },
  { title: "Workforce screening support", desc: "Support for informed hiring processes and workforce screening programs." },
  { title: "Coordination handled for you", desc: "TSM coordinates the process and helps you keep the documentation your business relies on." },
  { title: "Employer-first guidance", desc: "Plain-language guidance on what screening can and cannot tell you — no legal advice, ever." },
];

export default function ScreeningPage() {
  useEffect(() => {
    document.title = "TSM Background Screening | Workforce Screening Services | TSM Group CLE";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Business-focused background screening services designed to support workforce screening and informed hiring processes. Request screening services from TSM Group CLE in Northeast Ohio.");
  }, []);

  return (
    <div className="bg-paper" data-testid="screening-page">
      <PageHero
        testid="screening-hero"
        eyebrow="TSM4 — Available Now"
        title="TSM Background Screening"
        copy="Business-focused background screening services designed to support workforce screening and informed hiring processes — so you can make hiring decisions with confidence."
      >
        <div className="mt-9 flex flex-wrap gap-3">
          {["Available Now", "Business-focused", "Employer support included"].map((chip) => (
            <span key={chip} className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white/80">
              <CheckCircle2 className="h-3.5 w-3.5 text-gold" />
              {chip}
            </span>
          ))}
        </div>
      </PageHero>

      <section className="py-24 lg:py-32">
        <div className="container-tsm max-w-[1200px]">
          <FadeIn className="max-w-3xl">
            <p className="eyebrow">What Screening With TSM Includes</p>
            <h2 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl" data-testid="screening-includes-headline">
              Screening support built for employers.
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-5 sm:grid-cols-2" data-testid="screening-includes-grid">
            {INCLUDES.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.07} className="h-full">
                <div className="h-full rounded-xl border border-navy/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_34px_rgb(23,43,58,0.10)]">
                  <CheckCircle2 className="h-5 w-5 text-teal" />
                  <h3 className="mt-4 text-lg font-extrabold text-navy">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/65">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.15}>
            <p className="mt-10 max-w-3xl text-sm italic leading-relaxed text-ink/55">
              Screening scope, turnaround, and pricing are confirmed with you before any work begins. TSM Group CLE
              provides business services — it does not provide legal advice, and employers remain responsible for
              meeting applicable employment requirements.
            </p>
          </FadeIn>
        </div>
      </section>

      <ServiceRequestForm defaultService="Background Screening" />
    </div>
  );
}
